import {
  CheckOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined
} from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { neutral, semantic } from "../../../../components/widgets/tokens.ts"
import type { SaveStatus } from "../../redux/reportCanvas.types.ts"

const { Text } = Typography

interface ReportSaveIndicatorProps {
  status: SaveStatus
  error?: string | null
  onRetry: () => void
}

export const ReportSaveIndicator = ({
  status,
  error,
  onRetry
}: ReportSaveIndicatorProps) => {
  const { t } = useTranslation()

  if (status === "idle") return null

  if (status === "saving") {
    return (
      <Flex
        align="center"
        gap={4}
        data-testid="report-save-indicator"
        data-status={status}
      >
        <LoadingOutlined style={{ color: neutral.muted }} />
        <Text type="secondary">{t("reports.canvas.save.saving")}</Text>
      </Flex>
    )
  }

  if (status === "error") {
    return (
      <Flex
        align="center"
        gap={6}
        data-testid="report-save-indicator"
        data-status={status}
      >
        <Tooltip title={error || undefined}>
          <ExclamationCircleOutlined style={{ color: semantic.error }} />
        </Tooltip>
        <Text type="danger">{t("reports.canvas.save.error")}</Text>
        <Button
          type="link"
          size="small"
          onClick={onRetry}
          style={{ paddingInline: 0 }}
        >
          {t("reports.canvas.save.retry")}
        </Button>
      </Flex>
    )
  }

  if (status === "saved") {
    return (
      <Flex
        align="center"
        gap={4}
        data-testid="report-save-indicator"
        data-status={status}
      >
        <CheckOutlined style={{ color: semantic.success }} />
        <Text type="secondary">{t("reports.canvas.save.saved")}</Text>
      </Flex>
    )
  }

  return (
    <Flex
      align="center"
      gap={4}
      data-testid="report-save-indicator"
      data-status={status}
    >
      <Text type="secondary">{t("reports.canvas.save.pending")}</Text>
    </Flex>
  )
}
